import { parseToDateTime } from '@calendly/datetime'
import { boolean, number, object, string } from '@calendly/form/validation'
import { I18n } from '@calendly/i18n'

export const parseAndFormatDate = (dateString) => {
  const date = parseToDateTime(dateString)
  return date?.isValid ? date : null
}

const getValidationMessages = ({ useCrudValidationMessages = false } = {}) => {
  const crudKey = (key) => `publisher.event_type_editor_crud.payments.validation.${key}`

  return {
    codeRequired: useCrudValidationMessages
      ? I18n.t(crudKey('coupon_code_required'))
      : I18n.t('errors.messages.blank'),
    nameRequired: useCrudValidationMessages
      ? I18n.t(crudKey('coupon_name_required'))
      : I18n.t('errors.messages.blank'),
    percentageOffRequired: useCrudValidationMessages
      ? I18n.t(crudKey('percentage_off_required'))
      : I18n.t('errors.messages.blank'),
    fixedAmountOffRequired: useCrudValidationMessages
      ? I18n.t(crudKey('fixed_amount_off_required'))
      : I18n.t('errors.messages.blank'),
    usageLimitRequired: useCrudValidationMessages
      ? I18n.t(crudKey('usage_limit_required'))
      : I18n.t('errors.messages.blank'),
    expirationDateRequired: useCrudValidationMessages
      ? I18n.t(crudKey('expiration_date_required'))
      : I18n.t('errors.messages.blank'),
    expirationTimeRequired: useCrudValidationMessages
      ? I18n.t(crudKey('expiration_time_required'))
      : I18n.t('errors.messages.blank'),
    notANumber: I18n.t('errors.messages.not_a_number'),
    notAnInteger: I18n.t('errors.messages.not_an_integer'),
  }
}

export const buildValidationSchema = ({ useCrudValidationMessages = false } = {}) => {
  const messages = getValidationMessages({ useCrudValidationMessages })

  return object({
    name: string()
      .trim()
      .required(messages.nameRequired)
      .min(5, I18n.t('errors.messages.too_short', { count: 5 }))
      .max(64, I18n.t('errors.messages.too_long', { count: 64 })),
    code: string()
      .trim()
      .required(messages.codeRequired)
      .min(3, I18n.t('errors.messages.too_short', { count: 3 }))
      .max(64, I18n.t('errors.messages.too_long', { count: 64 })),
    type: string()
      .oneOf(['percentage', 'fixed'])
      .required(I18n.t('errors.messages.blank')),
    amount: number()
      .typeError(messages.notANumber)
      .when('type', {
        is: 'percentage',
        then: (schema) => schema
          .required(messages.percentageOffRequired)
          .min(1, I18n.t('errors.messages.greater_than_or_equal_to', { count: 1 }))
          .max(100, I18n.t('errors.messages.less_than_or_equal_to', { count: 100 }))
          .integer(messages.notAnInteger),
        otherwise: (schema) => schema
          .required(messages.fixedAmountOffRequired)
          .min(0.01, I18n.t('errors.messages.greater_than_or_equal_to', { count: 0.01 }))
          .max(100000, I18n.t('errors.messages.less_than_or_equal_to', { count: 100000 }))
      }),
    firstTimeOnly: boolean(),
    limitUsage: boolean(),
    usageLimit: number()
      .when('limitUsage', {
        is: true,
        then: (schema) => schema
          .typeError(messages.notANumber)
          .required(messages.usageLimitRequired)
          .min(1, I18n.t('errors.messages.greater_than_or_equal_to', { count: 1 }))
          .max(1000000, I18n.t('errors.messages.less_than_or_equal_to', { count: 1000000 }))
          .integer(messages.notAnInteger),
        otherwise: (schema) => schema.optional()
      }),
    addExpiration: boolean(),
    expirationTime: string()
      .when('addExpiration', {
        is: true,
        then: (schema) => schema.required(messages.expirationTimeRequired),
        otherwise: (schema) => schema.optional()
      }),
    expirationDate: string()
      .when('addExpiration', {
        is: true,
        then: (schema) => schema.required(messages.expirationDateRequired),
        otherwise: (schema) => schema.optional()
      })
  })
}
